import type { FullTranslation } from "../../lib/i18n"

// Shared by the FAQ block and the FAQPage JSON-LD on the landing page, so
// the structured data always says what the page actually shows.

export const FAQ_T = {
  eyebrow: { FR: "FAQ", EN: "FAQ" },
  headline: { FR: "Des questions ?", EN: "Questions?" },
  subhead: {
    FR: "L'essentiel sur les leads, les tarifs et la conformité. Si quelque chose manque, écrivez-nous.",
    EN: "The essentials on leads, pricing and compliance. If something is missing, write to us.",
  },
  contact_title: {
    FR: "Toujours une question ?",
    EN: "Still have a question?",
  },
  contact_body: {
    FR: "Réponse sous 24 h ouvrées, par un humain, pas un bot.",
    EN: "Answer within 24 business hours, from a human, not a bot.",
  },
  contact_cta: { FR: "Écrivez-nous", EN: "Write to us" },

  q_freshness: {
    FR: "Les leads sont-ils frais ?",
    EN: "Are the leads fresh?",
  },
  a_freshness: {
    FR: "Oui. Chaque batch est nouveau, dédupliqué contre l'historique complet de votre équipe : un business n'arrive jamais deux fois.",
    EN: "Yes. Every batch is new, deduplicated against your team's full history: a business never shows up twice.",
  },
  q_delivery: {
    FR: "Comment sont livrés les leads ?",
    EN: "How are leads delivered?",
  },
  a_delivery: {
    FR: "Chaque lundi matin : un CSV envoyé à votre équipe, plus un upsert dans HubSpot si connecté. Le dashboard affiche les leads livrés en temps réel.",
    EN: "Every Monday morning: a CSV sent to your team, plus a HubSpot upsert if connected. The dashboard shows delivered leads in real time.",
  },
  q_competitors: {
    FR: "Quelle différence avec Scrap.io / Apollo / Pharow ?",
    EN: "How is this different from Scrap.io / Apollo / Pharow?",
  },
  a_competitors: {
    FR: "Scrap.io vend du volume brut, Apollo et Pharow s'appuient sur des bases pré-indexées qui vieillissent vite sur les niches françaises. AutoLeads cherche en direct sur le web la combinaison niche + ville, enrichit chaque prospect (score SEO de leur site, détection de pubs payantes) et coûte 5 à 10 fois moins cher qu'un SDR IA type 11x ou Artisan.",
    EN: "Scrap.io sells raw volume, Apollo and Pharow rely on pre-indexed databases that age quickly on niche markets. AutoLeads searches the web live for your niche + city combination, enriches every prospect (their website's SEO score, paid ads detection) and costs 5 to 10 times less than an AI SDR like 11x or Artisan.",
  },

  q_plans: {
    FR: "Quelle est la différence entre Starter, Growth et Scale ?",
    EN: "What is the difference between Starter, Growth and Scale?",
  },
  a_plans: {
    FR: "Starter (249 €/mois, 2 500 crédits) couvre les besoins solo avec les filtres clés, l'enrichissement et les séquences email. Growth (499 €/mois, 5 500 crédits) ajoute plus de volume, jusqu'à 5 membres par équipe, des exports plus importants et le support prioritaire. Scale (899 €/mois, 10 800 crédits) vise le volume élevé, des membres illimités, l'API (à venir) et un accompagnement plus poussé. Les équipes sont illimitées sur toutes les offres. Les crédits fonctionnent de la même façon sur toutes les offres.",
    EN: "Starter (€249/mo, 2,500 credits) covers solo needs with the key filters, enrichment and email sequences. Growth (€499/mo, 5,500 credits) adds more volume, up to 5 members per team, larger exports and priority support. Scale (€899/mo, 10,800 credits) targets high volume, unlimited members, the API (coming soon) and hands-on onboarding. Teams are unlimited on every plan. Credits work the same way across every plan.",
  },
  q_overage: {
    FR: "Que se passe-t-il si je dépasse mon usage mensuel ?",
    EN: "What happens if I exceed my monthly usage?",
  },
  a_overage: {
    FR: "Vos leads en attente restent en file et reprennent dès le mois suivant. Vous pouvez aussi acheter un top-up ponctuel du montant de votre choix, sans renouvellement automatique. Aucune surfacturation surprise.",
    EN: "Your pending leads stay queued and resume the following month. You can also buy a one-time top-up of any amount, with no automatic renewal. No surprise overage charges.",
  },
  q_cancel: {
    FR: "Puis-je résilier à tout moment ?",
    EN: "Can I cancel anytime?",
  },
  a_cancel: {
    FR: "Oui. Résiliez en un clic depuis le portail Stripe. Vous gardez l'accès jusqu'à la fin de la période en cours, et le usage restant reste utilisable.",
    EN: "Yes. Cancel in one click from the Stripe portal. You keep access until the end of the current period, and any remaining usage stays available.",
  },
  q_resell: {
    FR: "Puis-je revendre les leads à mes clients (marque blanche) ?",
    EN: "Can I resell leads to my clients (white label)?",
  },
  a_resell: {
    FR: "Oui, sur le plan Scale. Les exports CSV et l'interface client sont brandés à vos couleurs (logo, nom de domaine, palette). Beaucoup d'agences web l'utilisent pour packager une offre d'acquisition pour leurs PME locales. Fonctionnalité prévue T+4 après votre souscription Scale.",
    EN: "Yes, on the Scale plan. CSV exports and the client interface carry your branding (logo, domain name, color palette). Many web agencies use it to package an acquisition offer for their local SMBs. Feature planned for T+4 after your Scale subscription.",
  },

  q_rgpd: {
    FR: "Vos données sont-elles légales (RGPD) ?",
    EN: "Is your data legal (GDPR)?",
  },
  a_rgpd: {
    FR: "Oui. Toutes les données viennent de sources publiques (sites pros, annuaires en ligne). La prospection B2B est autorisée sous intérêt légitime (art. 6.1.f). Voir la section Conformité ci-dessus pour le détail.",
    EN: "Yes. All data comes from public sources (business websites, online directories). B2B prospecting is allowed under legitimate interest (art. 6.1.f). See the Compliance section above for details.",
  },
  q_lemlist: {
    FR: "Peut-on remplacer Lemlist avec AutoLeads ?",
    EN: "Can AutoLeads replace Lemlist?",
  },
  a_lemlist: {
    FR: "Non. AutoLeads génère la copy et pousse les leads dans votre Lemlist ; l'envoi reste géré par Lemlist (qui gère la délivrabilité et le warm-up). C'est un partenariat fonctionnel, pas un concurrent.",
    EN: "No. AutoLeads generates the copy and pushes leads into your Lemlist; sending stays with Lemlist (which handles deliverability and warm-up). It is a functional partnership, not a competitor.",
  },
} satisfies FullTranslation

export const FAQ_ITEMS: { q: keyof typeof FAQ_T; a: keyof typeof FAQ_T }[] = [
  { q: "q_freshness", a: "a_freshness" },
  { q: "q_delivery", a: "a_delivery" },
  { q: "q_competitors", a: "a_competitors" },
  { q: "q_plans", a: "a_plans" },
  { q: "q_overage", a: "a_overage" },
  { q: "q_cancel", a: "a_cancel" },
  { q: "q_resell", a: "a_resell" },
  { q: "q_rgpd", a: "a_rgpd" },
  { q: "q_lemlist", a: "a_lemlist" },
]
